import { Membership, PermissionEnum, Workspace } from '@sr-sdks/api-sdk-axios';
import {
  QueryClient,
  UseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { queryOptionsGetMembership } from '../api/memberships/membershipApi';
import { queryOptionsGetWorkspace } from '../api/workspaces/workspaceApi';
import { TIME } from '../utils/helpers/common';
import { useCheckUserPermissions } from './useCheckUserPermissions';

/** Aligns with the app QueryClient default staleTime. */
const WORKSPACE_FRESHNESS_MS = TIME.minutesToMs(5);

/** In-flight refreshes shared across Sidebar + route-guard callers. */
const workspaceRefreshInFlight = new Map<string, Promise<void>>();

const isWorkspaceCacheFresh = (
  queryClient: QueryClient,
  workspaceId: string,
) => {
  const { queryKey } = queryOptionsGetWorkspace(workspaceId);
  const state = queryClient.getQueryState(queryKey);

  if (state?.status !== 'success' || !state.dataUpdatedAt) {
    return false;
  }

  return Date.now() - state.dataUpdatedAt < WORKSPACE_FRESHNESS_MS;
};

/**
 * Force a workspace fetch (not active-only invalidate) so a stale cached error
 * cannot be misread as a fresh failure. Coalesces concurrent callers.
 */
const ensureFreshWorkspace = (
  queryClient: QueryClient,
  workspaceId: string,
) => {
  if (isWorkspaceCacheFresh(queryClient, workspaceId)) {
    return Promise.resolve();
  }

  const existing = workspaceRefreshInFlight.get(workspaceId);

  if (existing) return existing;

  const promise = queryClient
    .fetchQuery({
      ...queryOptionsGetWorkspace(workspaceId),
      // Caller already decided the cache is stale for access checks — force a
      // network attempt even when the QueryClient default staleTime is Infinity.
      staleTime: 0,
    })
    .then(() => undefined)
    .finally(() => {
      workspaceRefreshInFlight.delete(workspaceId);
    });

  workspaceRefreshInFlight.set(workspaceId, promise);

  return promise;
};

interface WorkspaceMembershipResult {
  /**
   * True when a stale-workspace refresh was required for a user without
   * `ViewMemberships` but the refetch failed — callers must not trust the
   * cached `membershipStatus` in that case.
   */
  hasBlockingWorkspaceRefreshError: boolean;
  /** True when the membership record is actually being fetched for this workspace. */
  isEnabled: boolean;
  /**
   * True while access is still settling: membership pending, or a workspace
   * refresh for users who cannot read the membership record.
   */
  isResolving: boolean;
  query: UseQueryResult<Membership>;
  /** Clears the failure flag and re-runs the workspace refresh. */
  retryWorkspaceRefresh: () => void;
}

/**
 * Membership record for a workspace, fetched only when the current user is
 * allowed to read it.
 *
 * `GET /membership/:id` requires `ViewMemberships`, which the Workspace User
 * role does not have — firing it regardless guarantees a 403 on every page
 * load. When the fetch is skipped we refresh the workspace query (if stale)
 * instead so `membershipStatus` is not stuck behind the global staleTime after
 * staff approve/revoke (workspace users only ever see the workspace value).
 *
 * @param enabled Extra gate (e.g. a feature flag) applied on top of permission.
 */
export const useWorkspaceMembership = (
  workspace?: Workspace,
  enabled = true,
): WorkspaceMembershipResult => {
  const queryClient = useQueryClient();
  const canViewMemberships = useCheckUserPermissions(
    [PermissionEnum.ViewMemberships],
    undefined,
    workspace?._id,
  );

  const membershipId =
    enabled && canViewMemberships ? workspace?.membershipId : undefined;
  const isEnabled = Boolean(membershipId);
  const query = useQuery(queryOptionsGetMembership(membershipId));

  const workspaceId = workspace?._id;
  const shouldRefreshWorkspace = Boolean(
    enabled &&
    workspaceId &&
    !canViewMemberships &&
    !isWorkspaceCacheFresh(queryClient, workspaceId),
  );

  // Seed pending when a refresh is needed so nav/guards do not flash stale access.
  const [isRefreshingWorkspace, setIsRefreshingWorkspace] = useState(
    shouldRefreshWorkspace,
  );
  const [workspaceRefreshFailed, setWorkspaceRefreshFailed] = useState(false);
  const [refreshAttempt, setRefreshAttempt] = useState(0);

  const retryWorkspaceRefresh = useCallback(() => {
    // Set refreshing before clearing the failure flag so RequireCurrentMember
    // stays on the loader for at least one paint (no stale-membership flash).
    setIsRefreshingWorkspace(true);
    setWorkspaceRefreshFailed(false);
    setRefreshAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    if (!shouldRefreshWorkspace || !workspaceId) {
      setIsRefreshingWorkspace(false);
      setWorkspaceRefreshFailed(false);

      return;
    }

    let cancelled = false;

    setIsRefreshingWorkspace(true);
    setWorkspaceRefreshFailed(false);

    void ensureFreshWorkspace(queryClient, workspaceId)
      .catch(() => {
        if (!cancelled) setWorkspaceRefreshFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsRefreshingWorkspace(false);
      });

    return () => {
      cancelled = true;
    };
  }, [queryClient, refreshAttempt, shouldRefreshWorkspace, workspaceId]);

  const hasBlockingWorkspaceRefreshError = Boolean(
    enabled && workspaceId && !canViewMemberships && workspaceRefreshFailed,
  );

  return {
    hasBlockingWorkspaceRefreshError,
    isEnabled,
    isResolving:
      (isEnabled && query.isPending) ||
      (shouldRefreshWorkspace && isRefreshingWorkspace),
    query,
    retryWorkspaceRefresh,
  };
};
